import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';
import img_pages_home_lanhu_1 from '@/assets/images/pages-home-lanhu-1.png';
import img_pages_home_lanhu_2 from '@/assets/images/pages-home-lanhu-2.png';
import img_pages_home_lanhu_3 from '@/assets/svg/pages-home-lanhu-3.svg';
import img_pages_home_lanhu_4 from '@/assets/images/pages-home-lanhu-4.png';
import img_pages_home_lanhu_5 from '@/assets/svg/pages-home-lanhu-5.svg';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useProductStore, toRouteKey } from '@/store/product';
import SupportCards from '@/components/SupportCards';

const isProd = import.meta.env.PROD;
const cdnDomain = String(import.meta.env.VITE_CDN_DOMAIN ?? '').replace(/\/+$/, '');
const localUspVideoSrc = '/src/assets/video/home-video2.mp4';
const uspVideoSrc =
  isProd && cdnDomain ? `${cdnDomain}/assets/video/home-video2.mp4` : localUspVideoSrc;

const HERO_VIDEO_FILES = ['home-video5.mp4', 'home-video4.mp4', 'home-video3.mp4'];
const HERO_VIDEO_SRCS = HERO_VIDEO_FILES.map(fileName =>
  isProd && cdnDomain ? `${cdnDomain}/assets/video/${fileName}` : `/src/assets/video/${fileName}`
);
const HERO_CROSSFADE_MS = 900;

const NEW_ARRIVAL_PRODUCTS = [
  {
    name: ['Misty', 'Harbor'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Misty%20Harbor/Misty%20Harbor1.jpg`,
  },
  {
    name: ['Golden', 'Wheat'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Golden%20Wheat/Golden%20Wheat1.jpg`,
  },
  {
    name: ['Sunset', 'Glow'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Sunset%20Glow/Sunset%20Glow3.jpg`,
  },
  {
    name: ['Ivory', 'Grain'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Ivory%20Grain/Ivory%20Grain1.jpg`,
  },
  {
    name: ['Maple', 'Grove'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Maple%20Grove/Maple%20Grove1.jpeg`,
  },
  {
    name: ['Golden', 'Hour'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Golden%20Hour/Golden%20Hour1.jpeg`,
  },
  {
    name: ['Buttered', 'Wheat'],
    image: `${cdnDomain}/products/Spring%20Bloom%20Collection/Buttered%20Wheat/Buttered%20Wheat1.jpg`,
  },
  {
    name: ['Harvest', 'Moon', '7.5'],
    image: `${cdnDomain}/products/Spring%20Bloom%20Collection/Harvest%20Moon%207.5/Harvest%20Moon%207.5_1.jpg`,
  },
];

const Home = () => {
  const navigate = useNavigate();
  const collections = useProductStore(s => s.collections);

  const heroVideoRefs = useRef([]);
  const heroActiveLayerRef = useRef(0);
  const heroIndexRef = useRef(0);
  const heroPreloadedIndexRef = useRef(null);
  const heroPauseTimeoutRef = useRef(null);
  const [heroActiveLayer, setHeroActiveLayer] = useState(0);
  const [heroSegmentProgress, setHeroSegmentProgress] = useState(() =>
    HERO_VIDEO_SRCS.map(() => 0)
  );

  useEffect(() => {
    const firstVideo = heroVideoRefs.current[0];
    if (!firstVideo) return undefined;
    firstVideo.src = HERO_VIDEO_SRCS[0];
    firstVideo.play?.().catch(() => {});
    return () => {
      if (heroPauseTimeoutRef.current) clearTimeout(heroPauseTimeoutRef.current);
    };
  }, []);

  const preloadHeroVideo = index => {
    if (heroPreloadedIndexRef.current === index) return;
    const inactiveLayer = 1 - heroActiveLayerRef.current;
    const inactiveVideo = heroVideoRefs.current[inactiveLayer];
    if (!inactiveVideo) return;
    inactiveVideo.src = HERO_VIDEO_SRCS[index];
    inactiveVideo.load();
    heroPreloadedIndexRef.current = index;
  };

  const advanceHeroVideo = () => {
    const nextIndex = (heroIndexRef.current + 1) % HERO_VIDEO_SRCS.length;
    const outgoingLayer = heroActiveLayerRef.current;
    const incomingLayer = 1 - outgoingLayer;
    const incomingVideo = heroVideoRefs.current[incomingLayer];
    if (!incomingVideo) return;

    if (heroPreloadedIndexRef.current !== nextIndex) {
      incomingVideo.src = HERO_VIDEO_SRCS[nextIndex];
      incomingVideo.load();
    }
    incomingVideo.currentTime = 0;
    incomingVideo.play?.().catch(() => {});

    setHeroSegmentProgress(prev => {
      const next = [...prev];
      next[heroIndexRef.current] = 100;
      next[nextIndex] = 0;
      return next;
    });

    heroIndexRef.current = nextIndex;
    heroActiveLayerRef.current = incomingLayer;
    heroPreloadedIndexRef.current = null;
    setHeroActiveLayer(incomingLayer);

    const outgoingVideo = heroVideoRefs.current[outgoingLayer];
    if (heroPauseTimeoutRef.current) clearTimeout(heroPauseTimeoutRef.current);
    heroPauseTimeoutRef.current = setTimeout(() => {
      outgoingVideo?.pause();
    }, HERO_CROSSFADE_MS);
  };

  const handleHeroTimeUpdate = layerIdx => event => {
    if (layerIdx !== heroActiveLayerRef.current) return;
    const { currentTime, duration } = event.currentTarget;
    if (!duration) return;
    const percent = (currentTime / duration) * 100;
    setHeroSegmentProgress(prev => {
      const next = [...prev];
      next[heroIndexRef.current] = percent;
      return next;
    });
    if (duration - currentTime < 1) {
      preloadHeroVideo((heroIndexRef.current + 1) % HERO_VIDEO_SRCS.length);
    }
  };

  const handleHeroEnded = layerIdx => () => {
    if (layerIdx !== heroActiveLayerRef.current) return;
    advanceHeroVideo();
  };

  const productRouteMap = useMemo(() => {
    const normalize = value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    const map = new Map();
    for (const collection of collections ?? []) {
      const collectionKey = collection.routeKey ?? toRouteKey(collection.collection_name);
      if (!collectionKey) continue;
      for (const product of collection.products ?? []) {
        const productKey = product.routeKey ?? toRouteKey(product.product_name);
        const productName = normalize(product.product_name);
        if (!productKey || !productName || map.has(productName)) continue;
        map.set(productName, `/products/${collectionKey}/${productKey}`);
      }
    }
    return map;
  }, [collections]);
  const handleArrivalClick = nameLines => {
    const productName = nameLines.join(' ').trim().toLowerCase().replace(/\s+/g, ' ');
    const targetPath = productRouteMap.get(productName);
    if (targetPath) navigate(targetPath);
  };

  return (
    <div className={styles['home-page']}>
      <div className={styles.page}>
        {/* banner —— top */}
        <section className={`${styles['hero-section']} flex-col`}>
          <div className={styles['hero-video-stage']}>
            {[0, 1].map(layerIdx => (
              <video
                key={layerIdx}
                ref={el => {
                  heroVideoRefs.current[layerIdx] = el;
                }}
                className={`${styles['hero-video-layer']} ${
                  heroActiveLayer === layerIdx ? styles['is-active'] : ''
                }`}
                autoPlay={layerIdx === 0}
                muted
                playsInline
                preload={layerIdx === 0 ? 'auto' : 'none'}
                onTimeUpdate={handleHeroTimeUpdate(layerIdx)}
                onEnded={handleHeroEnded(layerIdx)}
              />
            ))}
          </div>
          <div className={styles['hero-progress']} aria-hidden="true">
            {HERO_VIDEO_SRCS.map((_, idx) => (
              <div key={idx} className={styles['hero-progress-seg']}>
                <span
                  style={{
                    width: `${Math.min(100, Math.max(0, heroSegmentProgress[idx]))}%`,
                  }}
                />
              </div>
            ))}
          </div>
          <div className={`${styles['hero-overlay']} flex-row align-center justify-center`}>
            <SiteTopNav />

            <div className={styles['hero-content']}>
              <h1 className={styles['hero-title']} data-motion="hero">
                Tradition Meets Innovation <br /> in Every Plank
              </h1>
              <p className={styles['hero-subtitle']} data-motion="copy">
                Timeless Wood. Thoughtfully Crafted. <br />
                Naturally Refined.
              </p>
              <button
                className={`${styles['hero-cta']} flex-row`}
                type="button"
                onClick={() => navigate('/products')}
                data-motion="card-soft"
                data-motion-interactive="press"
              >
                <span className={styles['hero-cta-label']}>view products</span>
                <img
                  className={styles['hero-cta-icon']}
                  alt=""
                  referrerPolicy="no-referrer"
                  src={img_pages_home_lanhu_5}
                />
              </button>
            </div>
          </div>
        </section>

        {/* why sakura floors */}
        <section
          className={styles['features-section']}
          data-motion="section"
          data-motion-threshold="0"
          data-motion-root-margin="0px 0px 0px 0px"
        >
          <h2
            className={styles['features-heading']}
            data-motion="title"
            data-motion-threshold="0"
            data-motion-root-margin="0px 0px 0px 0px"
          >
            why sakura floors
          </h2>
          <div className={styles['features-grid']}>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_1}
              />
              <h3 className={styles['feature-title']}>Japandi Design</h3>
              <p className={styles['feature-desc']}>
                Japanese craftsmanship meets Scandinavian simplicity. Natural materials, clean lines
                and quiet tones create warm, calm spaces built around balance and mindful living.
              </p>
            </article>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_2}
              />
              <h3 className={styles['feature-title']}>Guaranteed Quality</h3>
              <p className={styles['feature-desc']}>
                Every plank is finished and inspected at our own facilities in Japan, where
                traditional craft and modern technology meet before anything ships.
              </p>
            </article>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_4}
              />
              <h3 className={styles['feature-title']}>Wide Product Range</h3>
              <p className={styles['feature-desc']}>
                From warm Oak to bold Hickory, in thicknesses from 3/8″ to 5/8″ and widths from 3″
                to 9.5″, there&apos;s a floor for every space and style.
              </p>
            </article>
          </div>
        </section>

        {/* banner —— what makes us unique */}
        <section className={`${styles['usp-banner']} flex-col`}>
          <video
            className={styles['usp-video']}
            src={uspVideoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={uspVideoSrc} type="video/mp4" />
          </video>
          <div className={`${styles['usp-overlay']} flex-row`}>
            <div className={styles['usp-content']}>
              <h2 className={styles['usp-title']} data-motion="title">
                What Makes Us Unique
              </h2>
              <p className={styles['usp-text']} data-motion="copy">
                We create floors without constraint, offering true customization across dimensions,
                species, patterns, and finishes. Each made to order and crafted to reflect your
                vision. At the heart of our collections is premium Japanese Oak, celebrated for its
                refined grain, balanced character, and exceptional stability. Through meticulous
                craftsmanship and advanced finishing techniques, each plank is enhanced to reveal
                its natural beauty while embodying the quiet elegance of Japanese design. From
                carefully selected grain to harmonious tonal balance, every floor reflects a
                disciplined philosophy of precision, restraint, and enduring sophistication.
              </p>
            </div>
          </div>
        </section>

        {/* new arrivals — intro constrained; grid full-bleed */}
        <section className={`${styles['new-arrivals-section']} flex-col`}>
          <div className={styles['layout-container']}>
            <div
              className={`${styles['arrivals-intro']} flex-row justify-between`}
              data-motion="section"
            >
              <span className={styles['arrivals-title']}>
                New
                <br />
                Arrivals .
              </span>
              <span className={styles['arrivals-desc']}>
                Experience the perfect harmony of nature&apos;s beauty and modern innovation for a
                healthier, more beautiful home.
                <br />
                Redefining contemporary living with the rich textures and authentic character of
                premium hardwood.
              </span>
            </div>
          </div>
          <div className={styles['product-grid']}>
            {NEW_ARRIVAL_PRODUCTS.map((p, idx) => (
              <article
                key={`${p.name.join('-')}-${idx}`}
                className={styles['product-card']}
                data-motion="card"
                data-motion-group="new-arrivals-grid"
                onClick={() => handleArrivalClick(p.name)}
                style={{ backgroundImage: `url(${p.image})` }}
              >
                <h3 className={styles['product-name']} title={p.name.join(' ')}>
                  {p.name.map((line, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={`${line}-${i}`} className={styles['product-name-line']}>
                      {line}
                      {i < p.name.length - 1 ? <br /> : null}
                    </span>
                  ))}
                </h3>

                <button
                  className={styles['product-cta']}
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    handleArrivalClick(p.name);
                  }}
                  data-motion-interactive="press"
                >
                  <div className={styles['product-link']}>
                    view
                    <br />
                    more
                  </div>
                  <div className={styles['product-link-icon']} aria-hidden="true">
                    <img
                      className={styles['product-link-arrow']}
                      alt=""
                      referrerPolicy="no-referrer"
                      src={img_pages_home_lanhu_3}
                    />
                  </div>
                </button>
              </article>
            ))}
          </div>
        </section>

        {/* about + installation, care & warranty */}
        <section className={`${styles['about-section']} ${styles['layout-container']}`}>
          <h2 className={styles['about-title']} data-motion="title">
            about Sakura floors
          </h2>
          <p className={styles['about-text']} data-motion="copy">
            With years of expertise and experience, Sakura Floors is proud to be regarded as a
            manufacturing leader. Every decision is inspired by our main mission: to provide the
            highest standard of product and service to both retailers and customers, from
            installation day to years of everyday living.
          </p>
          <SupportCards className={styles['support-cards']} />
        </section>
      </div>
    </div>
  );
};

export default Home;
